import type {
  MatchingLevel,
  MatchingPanelDayDto,
  MatchingPreferenceDto,
  MatchingTargetType,
} from '../../api/matchingTypes';
import type { Role } from '../../api/types';

type Maybe<T> = T | null | undefined;

export const CLUB_TIME_ZONE = 'Europe/Moscow';
export const MAX_DAYS_AHEAD = 6;

/** Telegram-style id of the service account that owns an automatic table while it is being formed. */
export const AUTOMATIC_ORGANIZER_ID = '-1';
export const AUTOMATIC_ORGANIZER_NAME = 'Хранитель Тропы';

export function isAutomaticOrganizer(userId: Maybe<string | number>): boolean {
  return userId !== null && userId !== undefined && String(userId) === AUTOMATIC_ORGANIZER_ID;
}

/** Organizer or Admin ("club member"). */
export function isClubMember(role: Maybe<Role>): boolean {
  return role === 'organizer' || role === 'admin';
}

/** Organizer/Admin with a current club key. A plain User never counts, even with the flag set. */
export function isKeyHolder(user: Maybe<{ role: Role; hasClubKey: boolean }>): boolean {
  return !!user && isClubMember(user.role) && user.hasClubKey;
}

// ---------- dates (club zone) ----------

/** Current calendar date in the club time zone as YYYY-MM-DD. */
export function clubToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CLUB_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function addDaysToDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return next.toISOString().slice(0, 10);
}

/** Selectable game dates: today .. today + MAX_DAYS_AHEAD. */
export function selectableDates(today: string): string[] {
  return Array.from({ length: MAX_DAYS_AHEAD + 1 }, (_, i) => addDaysToDateKey(today, i));
}

export function isDateSelectable(dateKey: string, today: string): boolean {
  return dateKey >= today && dateKey <= addDaysToDateKey(today, MAX_DAYS_AHEAD);
}

/** HH:mm of an instant in the club time zone. */
export function formatClubTime(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: CLUB_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** "1 человек", "2 человека", "5 человек" (Russian plural rules). */
export function formatPeopleCount(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  const word = lastTwo >= 11 && lastTwo <= 14 ? 'человек' : last >= 2 && last <= 4 ? 'человека' : 'человек';
  return `${count} ${word}`;
}

export function formatClubWindow(startIso: string, endIso: string): string {
  const start = formatClubTime(startIso);
  const end = formatClubTime(endIso);
  return start === end ? start : `${start}–${end}`;
}

export function formatDateKeyRu(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}

// ---------- panel ----------

export type PanelState =
  | { kind: 'none'; activeCount: number; hasKeyHolder: boolean }
  | { kind: 'active'; progress: { size: number; min: number; max: number } | null }
  | { kind: 'matched'; eventId: number | null; confirmationDeadline: string | null }
  | { kind: 'formed'; eventId: number | null; startAt: string | null };

export function getPanelState(day: Maybe<MatchingPanelDayDto>): PanelState | null {
  if (!day) return null;
  const own = day.own;
  if (!own) return { kind: 'none', activeCount: day.activeCount, hasKeyHolder: day.hasKeyHolder };
  if (own.status === 'ACTIVE') return { kind: 'active', progress: own.progress };
  if (own.status === 'MATCHED') {
    return { kind: 'matched', eventId: own.eventId, confirmationDeadline: own.confirmationDeadline };
  }
  if (own.status === 'FORMED') return { kind: 'formed', eventId: own.eventId, startAt: own.startAt };
  return { kind: 'none', activeCount: day.activeCount, hasKeyHolder: day.hasKeyHolder };
}

/** Where a click on the panel leads. */
export function panelTarget(state: PanelState | null): string {
  if ((state?.kind === 'matched' || state?.kind === 'formed') && state.eventId != null) {
    return `/events/${state.eventId}`;
  }
  return '/gathering';
}

/** Whether the user already has a current (active/matched/formed) request on the date. */
export function hasCurrentRequestOnDate(
  panel: Record<string, MatchingPanelDayDto | undefined>,
  dateKey: string,
): boolean {
  return !!panel[dateKey]?.own;
}

// ---------- board ----------

export function groupByDate<T extends { gameDate: string }>(
  items: T[],
): { date: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const list = groups.get(item.gameDate);
    if (list) list.push(item);
    else groups.set(item.gameDate, [item]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, list]) => ({ date, items: list }));
}

// ---------- preferences ----------

export const LEVEL_LABEL: Record<MatchingLevel, string> = {
  WANT: 'Хочу',
  ACCEPTABLE: 'Буду',
  EXCLUDE: 'Не хочу',
};

export function groupPreferences(prefs: MatchingPreferenceDto[]): Record<MatchingLevel, MatchingPreferenceDto[]> {
  return {
    WANT: prefs.filter((p) => p.level === 'WANT'),
    ACCEPTABLE: prefs.filter((p) => p.level === 'ACCEPTABLE'),
    EXCLUDE: prefs.filter((p) => p.level === 'EXCLUDE'),
  };
}

/** Sets (or, with level = null, clears) the single level of a target. */
export function setPreference(
  prefs: MatchingPreferenceDto[],
  targetType: MatchingTargetType,
  targetId: number,
  level: MatchingLevel | null,
): MatchingPreferenceDto[] {
  const rest = prefs.filter((p) => !(p.targetType === targetType && p.targetId === targetId));
  return level ? [...rest, { targetType, targetId, level }] : rest;
}

export function getPreferenceLevel(
  prefs: MatchingPreferenceDto[],
  targetType: MatchingTargetType,
  targetId: number,
): MatchingLevel | null {
  return prefs.find((p) => p.targetType === targetType && p.targetId === targetId)?.level ?? null;
}

// ---------- form ----------

export type RequestFormInput = {
  date: string;
  startFrom: string;
  startTo: string;
  preferences: MatchingPreferenceDto[];
};

export type RequestFormErrors = {
  date?: string;
  time?: string;
  preferences?: string;
};

const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export function validateRequestForm(
  input: RequestFormInput,
  today: string,
  limits?: { maxGamePreferences: number; maxTagPreferences: number },
): RequestFormErrors {
  const errors: RequestFormErrors = {};

  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) {
    errors.date = 'Выберите дату';
  } else if (!isDateSelectable(input.date, today)) {
    errors.date = 'Можно выбрать дату от сегодня до +6 дней';
  }

  if (!TIME_RE.test(input.startFrom) || !TIME_RE.test(input.startTo)) {
    errors.time = 'Укажите время начала «с» и «до»';
  } else if (input.startFrom > input.startTo) {
    errors.time = 'Время «с» не может быть позже времени «до»';
  }

  if (limits) {
    const games = input.preferences.filter((p) => p.targetType === 'GAME').length;
    const tags = input.preferences.filter((p) => p.targetType === 'TAG').length;
    if (games > limits.maxGamePreferences || tags > limits.maxTagPreferences) {
      errors.preferences = `Не больше ${limits.maxGamePreferences} игр и ${limits.maxTagPreferences} тегов`;
    }
  }

  return errors;
}

export function hasErrors(errors: RequestFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Human message for a matching API error code. */
export function matchingErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'MATCHING_REQUEST_ALREADY_EXISTS':
      return 'У вас уже есть заявка на этот день';
    case 'MATCHING_OVERLAP':
      return 'Время пересекается с другим участием или заявкой';
    case 'MATCHING_DATE_INVALID':
      return 'Дата вне допустимого диапазона';
    case 'MATCHING_WINDOW_INVALID':
      return 'В выбранном окне нет подходящего времени начала';
    case 'MATCHING_PREVIEW_EXPIRED':
      return 'Окно изменилось — проверьте его ещё раз';
    case 'VERSION_MISMATCH':
      return 'Заявка уже изменилась — обновите страницу';
    case 'MATCHING_REQUEST_NOT_EDITABLE':
      return 'Эту заявку уже нельзя изменить';
    case 'MATCHING_PREFERENCE_LIMIT_EXCEEDED':
      return 'Слишком много предпочтений';
    case 'MATCHING_DISABLED_TARGET':
      return 'Выбранная игра или тег недоступны';
    case 'MATCHING_DISABLED':
      return 'Сбор игроков сейчас выключен';
    default:
      return fallback;
  }
}
